import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { TableCustom } from '../elements/TableCustom';
import { Image } from '../elements/Image';
import { ArchivedInspectionPhoto, getArchivedInspectionPhotosByRoom } from 'services/inspections';
import dayjs from 'dayjs';

const PHOTO_TYPE_BY_SOURCE: Record<string, string> = {
  SUBMITTED_LOCATION: 'Inspection location',
  ADDITIONAL_STRUCTURE: 'Additional structure',
  ASSET: 'Submitted asset',
  ROOM_LEVEL_ISSUE: 'Room-level issue',
  ISSUE_REPORT: 'Asset issue (report)',
  CUSTOM_ATTRIBUTE: 'Custom attribute'
};

function photoTypeLabel(row: ArchivedInspectionPhoto): string {
  const base = PHOTO_TYPE_BY_SOURCE[row.source] ?? row.sourceLabel ?? row.source;
  if (row.photoSubtype) return `${base} — ${row.photoSubtype}`;
  return base;
}

function entityIdDisplay(row: ArchivedInspectionPhoto): string {
  if (row.entityId == null) return '—';
  return String(row.entityId);
}

const HEADERS = [
  { label: 'ID', key: 'entityId', width: 120 },
  { label: 'Photo type', key: 'photoType', width: 340 },
  { label: 'Archived at', key: 'archivedAt', width: 160 },
  { label: 'Photo', key: 'photo', width: 300 }
];

export const TabArchivedPhotos = ({ roomId }: { roomId: number }) => {
  const [rows, setRows] = useState<ArchivedInspectionPhoto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!Number.isFinite(roomId) || roomId <= 0) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getArchivedInspectionPhotosByRoom(roomId);
        if (!cancelled) setRows(res.photos ?? []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  const tableData = useMemo(
    () =>
      rows.map((r) => {
        const mime = r.storageType?.trim() || 'image/jpeg';
        return {
          photoType: { value: photoTypeLabel(r) },
          entityId: { value: entityIdDisplay(r) },
          archivedAt: {
            value: r.archivedAt ? dayjs(r.archivedAt).format('YYYY-MM-DD HH:mm') : '—'
          },
          photo: {
            value: r.url ? <Image url={{ url: r.url, type: mime }} width={265} height={185} /> : '—'
          }
        };
      }),
    [rows]
  );

  if (loading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!Number.isFinite(roomId) || roomId <= 0) {
    return (
      <Typography color="text.secondary" py={2}>
        Missing location id for archived photos.
      </Typography>
    );
  }

  if (!rows.length) {
    return (
      <Typography color="text.secondary" py={2}>
        No archived photos for this inspection.
      </Typography>
    );
  }

  return (
    <Stack gap={1.5} width="100%" sx={{ minHeight: 0, flex: 1 }}>
      <Box
        sx={{
          width: '100%',
          maxHeight: 'calc(100vh - 280px)',
          minHeight: 200,
          overflowY: 'auto',
          overflowX: 'auto',
          pr: 0.5,
          WebkitOverflowScrolling: 'touch'
        }}>
        <TableCustom headers={HEADERS} data={tableData} handleChooseRow={() => {}} isScrollX />
      </Box>
    </Stack>
  );
};
